import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { Close, GetApp } from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:10000';

const AppDownloadDialog = ({ open, onClose }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setCode(value);
    setError('');
  };

  const handleVerify = async () => {
    if (code.length !== 4) {
      setError('Please enter a 4-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.post(`${API_BASE_URL}/api/app-download/verify`, { code });

      if (response.data.success && response.data.downloadUrl) {
        // Redirect to download URL
        window.location.href = response.data.downloadUrl;
        onClose();
        setCode('');
      } else {
        setError('Invalid code or download URL not configured');
      }
    } catch (err) {
      console.error('Error verifying code:', err);
      setError(err.response?.data?.error || 'Invalid access code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: { xs: 1, sm: 2 }
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GetApp color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Download App
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Enter the 4-digit access code to download the LMTSS mobile app
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Access Code"
          value={code}
          onChange={handleCodeChange}
          placeholder="0000"
          variant="outlined"
          inputProps={{
            maxLength: 4,
            pattern: '[0-9]*',
            inputMode: 'numeric',
            style: {
              fontSize: '1.5rem',
              fontWeight: 'bold',
              letterSpacing: '0.5em',
              textAlign: 'center'
            }
          }}
          disabled={loading}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleVerify();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleVerify}
          variant="contained"
          disabled={loading || code.length !== 4}
          startIcon={loading ? <CircularProgress size={20} /> : <GetApp />}
          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          {loading ? 'Verifying...' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppDownloadDialog;
